<template>
  <div>
    <v-row class="my-0" v-if="!isImpactValidated">
      <Alert
        class="mx-3"
        data-testid="reviewHeader-alert-implementCoachRole"
        v-if="
          isRevCompleted &&
          !canImplement &&
          !permissions.isSameSiteCoachOrAdmin &&
          !isImplemented
        "
        :text="$t('rev.implement_coach_role')"
        :vuetifyProps="{
          color: 'yellow',
          width: '100%',
          icon: 'mdi-alert-circle-outline',
        }"
      />
      <v-col cols="6" md="5" lg="5">
        <Subtitle
          :title="$t('rev.implement_title')"
          :description="$t('rev.implement_description')"
        />
      </v-col>
      <v-col cols="6" md="7" lg="7" class="d-flex justify-end">
        <div class="d-flex align-center mb-3">
          <ImplementCard
            v-if="isImplemented"
            :rev="rev"
            :canImplement="canImplement"
            :hasRequestValidation="hasRequestValidation"
            @removeImplementation="$emit('implementRev')"
          />
          <Button
            v-else
            rounded
            data-testid="implement-button"
            data-tourid="implement-button"
            color="primary"
            class="font-size-14"
            icon="mdi-rocket-launch"
            :loading="loading"
            :disabled="!canImplement && !permissions.isSameSiteCoachOrAdmin"
            :text="$t('rev.mark_as_implemented_button')"
            @click="$emit('implementRev')"
          />
        </div>
      </v-col>
    </v-row>
    <v-divider v-if="!isImpactValidated" class="mx-0 my-8" />
    <div class="my-0 d-flex">
      <ValidationRequestErrorAlert
        v-if="showRequestError"
        :text="validationRequestErrorText"
        @close="$emit('closeRequestError')"
      />

      <template v-else>
        <v-row class="my-0 py-0 mb-8">
          <v-col cols="6" md="5" lg="5">
            <Subtitle
              :title="$t('rev_cycle.review.impact')"
              :description="$t('rev.impact_description')"
            />
          </v-col>

          <v-col cols="6" md="7" lg="7" class="d-flex align-center justify-end">
            <div v-if="isImpactValidated" class="d-flex flex-column">
              <div class="align-center justify-end d-flex mb-4">
                <p
                  class="caption blue-grey--text text--darken-1 ma-0 pa-0 me-3"
                >
                  {{ $t('rev.validation.estimated_impact') }}
                </p>
                <v-switch
                  class="mt-0 pt-0"
                  dense
                  hide-details
                  :value="showEstimated"
                  @change="$emit('showEstimated')"
                />
              </div>
              <ImpactCard
                :rev="rev"
                :canDoFinancialValidation="canDoFinancialValidation"
                @validate="$emit('validate')"
              />
            </div>
            <RequestValidationCard
              v-if="hasRequestValidation && !isImpactValidated"
              :rev="rev"
              :canDoFinancialValidation="canDoFinancialValidation"
              @cancelValidationRequest="
                $emit('cancelValidationRequest', $event)
              "
              @validate="$emit('validate')"
            />
            <template v-if="!isImpactValidated && !hasRequestValidation">
              <v-tooltip
                v-if="!isCompleted || !isImplemented"
                top
                max-width="250px"
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-on="on"
                    size="24"
                    color="blue-grey darken-1"
                    class="me-1"
                    data-testid="reviewHeader-icon-requestValidationTooltip"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <p class="text-center font-size-14 ma-0 pa-0">
                  {{ $t('rev.validation.request_validation_tooltip') }}
                </p>
              </v-tooltip>
              <Button
                color="primary"
                data-testid="reviewHeader-button-requestValidation"
                :disabled="!isCompleted || !isImplemented"
                :text="$t('rev.validation.request_validation')"
                @click="$emit('handleRequestValidationClick')"
              />
            </template>
          </v-col>
        </v-row>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import mixin from '@/mixins/mixin'
import revPermissionsMixin from '@/mixins/revPermissionsMixin'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import Subtitle from '@/components/Rev/PhaseSection/Subtitle.vue'
import ValidationRequestErrorAlert from '@/components/Rev/Review/ValidationRequestErrorAlert.vue'
import Button from '@/components/UI/Button.vue'
import Alert from '@/components/Alert.vue'

import ImpactCard from './ValidationCard/ImpactCard'
import RequestValidationCard from './ValidationCard/RequestValidationCard'
import ImplementCard from './ValidationCard/ImplementCard'

export default {
  name: 'ReviewHeader',

  components: {
    ImpactCard,
    RequestValidationCard,
    ImplementCard,
    Button,
    Subtitle,
    ValidationRequestErrorAlert,
    Alert,
  },

  mixins: [trackerMixin, mixin, revPermissionsMixin],

  props: {
    validationRequestErrorText: {
      type: String,
      default: '',
    },
    rev: {
      type: Object,
      required: true,
    },
    showEstimated: {
      type: Boolean,
      default: false,
    },
    isImpactValidated: Boolean,
    isImplemented: Boolean,
    hasRequestValidation: Boolean,
    showRequestError: {
      type: Boolean,
      default: false,
    },
    loading: Boolean,
    canImplement: Boolean,
    canDoFinancialValidation: Boolean,
    isCompleted: Boolean,
  },

  computed: {
    ...mapGetters(['account', 'organizationSiteGroups']),

    permissions() {
      return this.getPermissions({
        rev: this.rev,
        account: this.account,
        organizationSiteGroups: this.organizationSiteGroups,
      })
    },

    isRevCompleted() {
      return (
        this.isCompleted ??
        this.rev?.status === this.$constants.revStatus.completed
      )
    },
  },
}
</script>
